import { DateTime } from 'luxon'
import {
  getName,
  isNotDefined,
  type ReportColumnValue,
  type ReportKpiInput,
  type ReportTimePeriod,
  type TeamId,
  type TinyCompany,
  type TinyTeam,
  type TinyUser,
  type UserId,
} from '@terros/common'

export const toValidNumberValue = (value: ReportColumnValue | undefined): number => {
  if (isNotDefined(value) || typeof value !== 'number') return 0
  return value
}

export const stringifyReportNumberValue = (value: ReportColumnValue | undefined): string => {
  return toValidNumberValue(value).toLocaleString()
}

export const parseToValidNumberValue = (value: ReportColumnValue): number => {
  if (isNotDefined(value) || typeof value !== 'number') throw new Error('Invalid value, expected a number')
  return value
}

export const parseToValidEntity = (entity: ReportColumnValue): TinyUser | TinyTeam | TinyCompany => {
  if (isNotDefined(entity) || typeof entity !== 'object') throw new Error('Invalid entity, expected an object')
  if ('userId' in entity) return entity
  if ('teamId' in entity) return entity
  if ('companyId' in entity) return entity
  throw new Error('Invalid entity, expected a user, team or company')
}

const toGroupByTime = (startTime: number, period: ReportTimePeriod): Extract<ReportStatGroupBy, { type: 'time' }> => {
  const start = DateTime.fromMillis(startTime)
  const end = start.plus({ [period]: 1 })
  const startString = start.toLocaleString(DateTime.DATETIME_SHORT)
  const endString = end.toLocaleString(DateTime.DATETIME_SHORT)
  return { type: 'time', value: `${startString} - ${endString}`, period: { startTime, endTime: end.toMillis() } }
}

export type ReportStatGroupBy =
  | {
      type: 'user'
      user: TinyUser
    }
  | {
      type: 'team'
      team: TinyTeam
    }
  | {
      type: 'company'
      company: TinyCompany
    }
  | {
      type: 'time'
      value: string
      period: {
        startTime: number
        endTime: number
      }
    }
  | {
      type: 'other'
      value: string | number
    }

export const toReportStatGroupBy = (
  groupBy: ReportColumnValue | undefined,
  grouping: ReportKpiInput['grouping']
): ReportStatGroupBy => {
  if (isNotDefined(groupBy)) throw new Error('GroupBy cannot be undefined')
  if (grouping.type === 'time') {
    if (typeof groupBy !== 'number') throw new Error('ReportKPI grouping by time. Expected row groupBy to be a number')
    return toGroupByTime(groupBy, grouping.period)
  }
  if (typeof groupBy === 'object') {
    if ('userId' in groupBy) return { type: 'user', user: groupBy }
    if ('teamId' in groupBy) return { type: 'team', team: groupBy }
    if ('companyId' in groupBy) return { type: 'company', company: groupBy }
  }
  if (typeof groupBy === 'boolean') throw new Error('Unexpected boolean groupBy')
  return { type: 'other', value: groupBy.toLocaleString() }
}

export type ReportGroupEntity = TinyUser | TinyTeam

export const toReportGroupEntity = (value: ReportColumnValue | undefined): ReportGroupEntity | undefined => {
  if (isNotDefined(value) || typeof value !== 'object') return undefined
  if ('userId' in value) return value
  if ('teamId' in value) return value
  return undefined
}

export const getReportGroupEntityId = (entity: ReportGroupEntity): UserId | TeamId => {
  if ('userId' in entity) return entity.userId
  return entity.teamId
}

export const getReportGroupEntityName = (entity: ReportGroupEntity): string => {
  if ('userId' in entity) return getName(entity)
  return entity.name
}
